import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Choice } from '@/components/Choice'
import { FormError } from '@/components/FormError'
import { PersonSelect } from '@/components/PersonSelect'
import { QrCode } from '@/components/QrCode'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'
import type { Family } from '@/lib/families'
import { firstName, useTree } from '@/lib/tree'

type Way = 'qr' | 'link' | 'email'
type Made = { token: string; expiresAt: number; name: string; show: 'qr' | 'link' }

// Inviting one person: who they are (picked from the tree, so they'll be asked "Is this you?", or just their name),
// whether they're joining your home, and how the invite reaches them: shown as a QR code to scan there and then, a
// link to send however you like, or an email from us. Every way works once, for 7 days. Opened from a person's page,
// it's already for them.
export function InviteForm({ family, person }: { family: Family; person?: { id: string; name: string } }) {
  const queryClient = useQueryClient()
  const tree = useTree(family.id)
  const [personId, setPersonId] = useState(person?.id ?? '')
  const [name, setName] = useState('')
  const [way, setWay] = useState<Way>('email')
  const [email, setEmail] = useState('')
  const [home, setHome] = useState<'own' | 'mine'>('own')
  const [sentTo, setSentTo] = useState('')
  const [made, setMade] = useState<Made | null>(null)
  const unclaimed = (tree.data?.people ?? []).filter((p) => p.living && !p.pet && !p.linked)
  const picked = person ?? unclaimed.find((p) => p.id === personId)
  const who = picked?.name ?? name.trim()

  const invite = useMutation({
    mutationFn: () =>
      api<{ token?: string; expiresAt?: number }>(`/families/${family.id}/invites`, {
        method: 'POST',
        body: {
          ...(way === 'email'
            ? { via: 'email', email: email.trim() }
            : { via: 'link', name: picked ? undefined : who }),
          personId: picked?.id,
          intoMyHome: home === 'mine',
        },
      }),
    onSuccess: async (res) => {
      if (way === 'email') {
        setSentTo(email.trim())
        setEmail('')
      } else if (res.token && res.expiresAt) {
        setMade({ token: res.token, expiresAt: res.expiresAt, name: who, show: way })
      }
      if (!person) setPersonId('')
      setName('')
      await queryClient.invalidateQueries({ queryKey: ['family', family.id] })
    },
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    setSentTo('')
    invite.mutate()
  }

  if (made) return <Share made={made} family={family} onAgain={() => setMade(null)} />

  return (
    <form onSubmit={submit} className="grid gap-5">
      {!person && unclaimed.length > 0 && (
        <div className="grid gap-2">
          <Label htmlFor="invite-person">Who are they on the tree?</Label>
          <PersonSelect
            id="invite-person"
            placeholder="Not on the tree, or not sure"
            none="Not on the tree, or not sure"
            people={unclaimed}
            value={personId}
            onChange={setPersonId}
          />
          <p className="text-sub text-muted-foreground">They’ll be asked “Is this you?” when they join.</p>
        </div>
      )}
      {!picked && way !== 'email' && (
        <div className="grid gap-2">
          <Label htmlFor="invite-name">Their name</Label>
          <Input id="invite-name" required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
      )}
      {family.home && (
        <Choice
          legend="Where will they live in the app?"
          value={home}
          options={[
            ['own', 'A home of their own'],
            ['mine', `With you, in ${family.home.name}`],
          ]}
          onChange={(v) => setHome(v as 'own' | 'mine')}
        />
      )}
      <Choice
        legend={`How should ${who ? firstName(who) : 'they'} get it?`}
        hint={
          way === 'qr'
            ? 'They’re with you: they scan it with their phone’s camera.'
            : way === 'link'
              ? 'Send it in a text, WhatsApp, anywhere. Whoever opens it first can use it.'
              : 'We email them a link that only works for that address.'
        }
        value={way}
        options={[
          ['email', 'Email'],
          ['link', 'Link'],
          ['qr', 'QR code'],
        ]}
        onChange={(v) => setWay(v as Way)}
      />
      {way === 'email' && (
        <div className="grid gap-2">
          <Label htmlFor="invite-email">Their email</Label>
          <Input id="invite-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
      )}
      <FormError error={invite.error} />
      <Button type="submit" disabled={invite.isPending} className="self-start">
        {way === 'email' ? 'Send invite' : way === 'link' ? 'Make the link' : 'Show the QR code'}
      </Button>
      {sentTo && (
        <p role="status" className="text-sm text-muted-foreground">
          Invite sent to {sentTo}. The link works for 7 days.
        </p>
      )}
    </form>
  )
}

// The made invite, as a QR code to scan or a link to send. Its secret is only here, so closing this is the last
// chance to see it; making another is always possible.
function Share({ made, family, onAgain }: { made: Made; family: Family; onAgain: () => void }) {
  const [show, setShow] = useState(made.show)
  const [copied, setCopied] = useState(false)
  const link = `${location.origin}/join/${made.token}`
  const first = firstName(made.name)
  const until = new Date(made.expiresAt).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
  const canShare = typeof navigator.share === 'function'

  async function copy() {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
    } catch {
      // Some browsers refuse; the link is on screen to copy by hand.
    }
  }

  async function share() {
    try {
      await navigator.share({ title: `Join ${family.name}`, text: `Join ${family.name} on gatheredfrom`, url: link })
    } catch {
      // Closing the share sheet without picking anything is fine.
    }
  }

  return (
    <div className="grid gap-5">
      {show === 'qr' ? (
        <div className="grid justify-items-center gap-3 text-center">
          <p className="text-lg font-semibold tracking-tight">Have {first} scan this</p>
          <p className="text-sub text-muted-foreground">
            With their phone’s camera. It opens gatheredfrom and asks them to sign in.
          </p>
          <QrCode text={link} label={`QR code to join ${family.name}`} className="w-64 max-w-full rounded-xl" />
        </div>
      ) : (
        <div className="grid gap-2">
          <Label htmlFor="invite-link">The link for {first}</Label>
          <Input id="invite-link" readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
          <div className="flex flex-wrap gap-2">
            {canShare && (
              <Button type="button" onClick={share}>
                Send the link
              </Button>
            )}
            <Button type="button" variant={canShare ? 'outline' : 'default'} onClick={copy}>
              {copied ? 'Copied' : 'Copy the link'}
            </Button>
          </div>
        </div>
      )}
      <p role="status" className="text-sub text-muted-foreground">
        For {made.name}. Works once, until {until}. You’ll see who joined, and can take them out if it wasn’t {first}.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={() => setShow(show === 'qr' ? 'link' : 'qr')}>
          {show === 'qr' ? 'Send as a link instead' : 'Show as a QR code'}
        </Button>
        <Button type="button" variant="ghost" onClick={onAgain}>
          Invite someone else
        </Button>
      </div>
    </div>
  )
}
